import React, { useEffect } from 'react';
import formation1 from '../../assets/images/formation/formation2.jpg';
import formation2 from '../../assets/images/formation/formation1.jpg';
import formation3 from '../../assets/images/formation/formation3.jpg';
import ministry1 from '../../assets/images/ministry/p1.jpg';
import ministry2 from '../../assets/images/ministry/p2.jpg';
import ministry3 from '../../assets/images/ministry/p3.jpg';
import ministry4 from '../../assets/images/ministry/p4.jpg';
import ministry5 from '../../assets/images/ministry/p5.jpg';
import interreligiousimg from '../../assets/images/inter-religious-faith.jpg';
import { Helmet } from "react-helmet-async";
import { Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Formation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-5" style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <Helmet>
        <title>Theological Formation - Vidyajyoti College of Theology</title>
        <meta name="description" content="Theological Formation of Jesuits, Seminarians, Women Religious, and Laity at Vidyajyoti College of Theology, Delhi." />
        <meta name="keywords" content="Theological formation, Jesuit theology, Seminary Delhi, Women Religious Theology, Lay Formation" />
        <link rel="canonical" href="/about/formation" />
      </Helmet>

      <div className="container">
        {/* Page Header */}
        <div className="text-center mx-auto pb-5" style={{ maxWidth: '800px' }}>
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3" style={{ background: 'rgba(121, 3, 29, 0.08)', color: '#79031d', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(121, 3, 29, 0.2)' }}>
            <Sparkles size={14} /> Integral Pedagogy
          </div>
          <h1 className="display-6 fw-bold mb-3" style={{ color: '#1a2b56', fontFamily: "'Outfit', sans-serif" }}>
            Theological Formation
          </h1>
          <p className="lead text-muted" style={{ fontSize: '1.05rem', lineHeight: '1.7' }}>
            Forming men and women into reflective, compassionate ministers of the Gospel through rigorous academic study, lived communion with marginalized communities, and deep inter-faith dialogue.
          </p>
        </div>

        {/* Section 1: Jesuits & Seminarians */}
        <motion.div 
          className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-5"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="row align-items-center g-4">
            <div className="col-lg-6">
              <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: '#1a2b56', color: '#fff' }}>Jesuits &amp; Seminarians</span>
              <h3 className="fw-bold mb-3" style={{ color: '#1a2b56' }}>Formation of Jesuits, Seminarians &amp; Men Religious</h3>
              <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                Pope Francis desires priests to be &ldquo;shepherds with the smell of the sheep.&rdquo; In accordance with <em>Optatam Totius</em>, pastoral concern thoroughly permeates the entire training at Vidyajyoti. While a major part of the student body comprises Jesuit Scholastics from the South Asian Assistancy, a sizable number join from Pratiksha (diocesan seminary), IMS Seminary, and other religious congregations including MCBS, CST, MSFS, SG, and SVD.
              </p>
              <p className="text-muted mb-0" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                After foundational studies in Regional Theology Centres (JFCTs), students gather at Vidyajyoti for integrative theology, synthesizing diverse regional cultures and apostolic experiences.
              </p>
            </div>
            <div className="col-lg-6">
              <div className="rounded-4 overflow-hidden shadow-sm" style={{ height: '320px' }}>
                <img src={formation1} alt="Theological formation of seminarians and Jesuits" className="w-100 h-100" style={{ objectFit: 'cover' }} />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 2: Women Religious */}
        <motion.div 
          className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-5"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="row align-items-center g-4 flex-lg-row-reverse">
            <div className="col-lg-6">
              <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: '#79031d', color: '#fff' }}>Women Religious</span>
              <h3 className="fw-bold mb-3" style={{ color: '#79031d' }}>Theological Formation of Women Religious</h3>
              <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                Since opening its doors to women in 1974, over 300 women religious have completed theological studies at Vidyajyoti, with several achieving M.Th. and Ph.D. degrees. Most alumni hold leadership and formation roles in their respective congregations across Asia, creating a powerful multiplier effect.
              </p>
              <p className="text-muted mb-0" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                Vidyajyoti is among the pioneering faculties to appoint women as full-time permanent professors. Notable faculty include Prof. Sr. Shalini Mulakkal PBVM (first woman President of the Indian Theological Association) and Sr. Prabina Rudam IBVM.
              </p>
            </div>
            <div className="col-lg-6">
              <div className="rounded-4 overflow-hidden shadow-sm" style={{ height: '320px' }}>
                <img src={formation2} alt="Women Religious studying theology at Vidyajyoti" className="w-100 h-100" style={{ objectFit: 'cover' }} />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 3: Formation of Laity */}
        <motion.div 
          className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-5"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="row align-items-center g-4">
            <div className="col-lg-6">
              <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: '#c69214', color: '#1a2b56', fontWeight: 600 }}>Lay Faithful</span>
              <h3 className="fw-bold mb-3" style={{ color: '#1a2b56' }}>Theological Formation of the Laity</h3>
              <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                Vidyajyoti has always believed theology must transcend traditional confines. Lay women and men have pursued B.Th., Diploma, and VJ-DEPTh distance courses side-by-side with clerical students. This shared classroom dynamic enriches both groups, grounding theological reflection in real-world professional and family life.
              </p>
              <p className="text-muted mb-0" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                Distinguished lay scholars also regularly lecture at Vidyajyoti, enriching the curriculum with insights from history, sociology, and philosophy.
              </p>
            </div>
            <div className="col-lg-6">
              <div className="rounded-4 overflow-hidden shadow-sm" style={{ height: '320px' }}>
                <img src={formation3} alt="Formation of Catholic lay persons in theology" className="w-100 h-100" style={{ objectFit: 'cover' }} />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 4: Theologizing with the Poor */}
        <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 mb-5" style={{ background: 'linear-gradient(135deg, #1a2b56 0%, #2a3f75 100%)', color: '#fff' }}>
          <div className="text-center mx-auto mb-5" style={{ maxWidth: '750px' }}>
            <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(198,146,20,0.25)', color: '#ffd56b', fontWeight: 600 }}>Core Pedagogical Pillar</span>
            <h2 className="fw-bold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Theologizing with the Marginalized &amp; the Poor
            </h2>
            <p className="text-light opacity-90" style={{ lineHeight: '1.7' }}>
              Fr. Pedro Arrupe SJ envisioned Vidyajyoti as a center of theological reflection on the challenges of the contemporary world, where lived contact with the poor triggers deep cognitive, attitudinal, and spiritual conversion.
            </p>
          </div>

          <div className="row g-4">
            {[
              {
                img: ministry1,
                title: "Pedagogy of Contextual Theology",
                desc: "Fieldwork is an integral requirement for B.Th. studies, forging an alliance with Dalit, Adivasi, and subaltern communities."
              },
              {
                img: ministry2,
                title: "Urban Slums & Rickshaw Pullers",
                desc: "Active presence in Delhi resettlement colonies, TB and leprosy care centres, and Asha ki Kiran rickshaw puller fellowship."
              },
              {
                img: ministry3,
                title: "Human Dignity & Conscientization",
                desc: "Evening tuition centers, women empowerment collectives, motivational workshops, and prison ministry in Tihar Jail."
              },
              {
                img: ministry4,
                title: "Crisis Relief & Solidarity",
                desc: "Frontline relief in national disasters (Uttarkashi, Kashmir, Orissa, Nepal) and peace yatras such as Karwan-e-Mohabbat."
              },
              {
                img: ministry5,
                title: "Double Baptism: Poor & Religions",
                desc: "Living out Aloysius Pieris’s vision of immersion into both Asian poverty and diverse religious traditions."
              }
            ].map((item, idx) => (
              <div key={idx} className={idx < 2 ? "col-md-6" : "col-md-4"}>
                <div className="card h-100 border-0 rounded-4 overflow-hidden bg-white text-dark shadow-sm">
                  <div style={{ height: '200px' }}>
                    <img src={item.img} alt={item.title} className="w-100 h-100" style={{ objectFit: 'cover' }} />
                  </div>
                  <div className="p-3.5 p-md-4">
                    <h5 className="fw-bold mb-2" style={{ color: '#1a2b56', fontSize: '1.05rem' }}>{item.title}</h5>
                    <p className="text-muted small mb-0" style={{ lineHeight: '1.6' }}>{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 5: Immersion into Other Faith Dimensions */}
        <motion.div 
          className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="row align-items-center g-4">
            <div className="col-lg-6">
              <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: '#79031d', color: '#fff' }}>Dialogue of Life</span>
              <h3 className="fw-bold mb-3" style={{ color: '#79031d' }}>Immersion into Other Faith Traditions</h3>
              <p className="text-muted" style={{ lineHeight: '1.8', textAlign: 'justify' }}>
                Students look at neighboring religious traditions not as detached objects of study, but with profound reverence for their spiritual, cultural, and philosophical heritage. Immersions in Varanasi, Haridwar, and Rishikesh open doors to contemplative encounters inspired by Abhishiktananda and Vandana Mataji.
              </p>
              <div className="p-3 rounded-3 mt-3" style={{ background: 'rgba(26,43,86,0.04)', borderLeft: '4px solid #1a2b56' }}>
                <p className="small mb-0 fst-italic text-dark">
                  &ldquo;The world&rsquo;s religious traditions represent not the search of people for God, but God&rsquo;s search for them.&rdquo; — Jacques Dupuis SJ
                </p>
              </div>
            </div>
            <div className="col-lg-6 text-center">
              <div className="rounded-4 shadow-sm p-2 p-md-3 bg-light d-flex align-items-center justify-content-center">
                <img 
                  src={interreligiousimg} 
                  alt="Inter-religious dialogue and faith immersion" 
                  className="img-fluid rounded-3" 
                  style={{ 
                    maxHeight: '380px', 
                    maxWidth: '100%', 
                    width: 'auto', 
                    height: 'auto', 
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto' 
                  }} 
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
